import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Eye, EyeOff, Mail, Lock, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface LoginFormProps {
  formData: { email: string; password: string };
  showPassword: boolean;
  isLoading: boolean;
  isLocked: boolean;
  resetMessage: string;
  resetEmailSent: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onInputChange: (field: string, value: string) => void;
  onTogglePassword: () => void;
  onPasswordReset: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  formData,
  showPassword,
  isLoading,
  isLocked,
  resetMessage,
  resetEmailSent,
  onSubmit,
  onInputChange,
  onTogglePassword,
  onPasswordReset,
}) => {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="space-y-4 sm:space-y-6">
        <div className="space-y-3 sm:space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-sm font-medium text-gray-700"
            >
              {t("common:Email")}
            </Label>
            <div className="relative">
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder={t("common:Enter your Email")}
                value={formData.email}
                onChange={(e) => onInputChange("email", e.target.value)}
                className="pl-10 h-11 sm:h-12 text-sm sm:text-base border-2 focus:border-purple-500 focus:ring-purple-200"
                disabled={isLoading || isLocked}
              />
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            </div>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="text-sm font-medium text-gray-700"
            >
              {t("common:Password")}
            </Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder={t("common:Enter your Password")}
                value={formData.password}
                onChange={(e) => onInputChange("password", e.target.value)}
                className="pl-10 pr-10 h-11 sm:h-12 text-sm sm:text-base border-2 focus:border-purple-500 focus:ring-purple-200"
                disabled={isLoading || isLocked}
              />
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <button
                type="button"
                onClick={onTogglePassword}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={isLoading || isLocked}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Button
        type="submit"
        className="w-full h-12 text-sm font-semibold"
        disabled={isLoading || isLocked}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
            {t("common:Sign In")}
          </>
        ) : (
          <>
            <LogIn className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
            {t("common:Sign In")}
          </>
        )}
      </Button>

      <div className="text-center space-y-2">
        <button
          type="button"
          onClick={onPasswordReset}
          className="text-sm text-purple-600 hover:text-purple-800 underline"
          disabled={isLoading || isLocked}
        >
          {t("common:Forgot your password?")}
        </button>
      </div>


      {/* Password Reset Message */}
      {resetMessage && (
        <div className={`mt-4 p-3 rounded-md text-sm ${resetEmailSent ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {resetMessage}
        </div>
      )}
    </form>
  );
};
